import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";
import { NAV_IDS } from "@/lib/constants";
import { useLang } from "@/lib/i18n";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import logo from "@/assets/logo.png";

export default function Navbar({ bannerVisible = false }) {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        style={{ top: bannerVisible ? 44 : 0 }}
        className={`fixed inset-x-0 z-50 transition-[background-color,color,border-color,top] duration-500 ${
          scrolled
            ? "bg-[#FDFBF7]/90 backdrop-blur-md border-b border-bordeaux/10 text-ink"
            : "bg-transparent text-ivory"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <button
            data-testid="nav-brand"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-baseline gap-2"
          >
            <img src={logo} alt="Gelateria Imperiale" data-testid="nav-logo" className="h-11 w-auto" />
          </button>

          <nav className="hidden items-center gap-6 lg:flex">
            {NAV_IDS.map((id) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="font-mono-alt text-[11px] uppercase tracking-[0.22em] opacity-80 transition-opacity duration-300 hover:opacity-100"
              >
                {t.nav[id]}
              </button>
            ))}
            <LanguageSwitcher dark={!scrolled} />
          </nav>

          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSwitcher dark={!scrolled} />
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-bordeaux px-8 py-6 text-ivory"
          >
            <div className="flex items-center justify-between">
              <img src={logo} alt="Gelateria Imperiale" className="h-14 w-auto" />
              <button data-testid="nav-mobile-close-button" onClick={() => setOpen(false)} aria-label="Chiudi menu">
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-5">
              {NAV_IDS.map((id, i) => (
                <motion.button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.4 }}
                  onClick={() => go(id)}
                  className="text-left font-display text-4xl font-medium"
                >
                  {t.nav[id]}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
